import {
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import React, {
  useEffect,
  useMemo,
} from 'react';
import { Field } from 'react-final-form';
import {
  RootStateOrAny,
  useSelector,
} from 'react-redux';
import useRequest from 'shared/src/hooks/useRequest';
import PageHeader from 'web-react-ui/src/chakra/page/PageHeader';
import VideoUploader from 'web-react-ui/src/components/video/VideoUploader';
import DateTimeField from 'web-react-ui/src/reactFinalForm/fields/dateTimeField/DateTimeField';
import FieldError from 'web-react-ui/src/reactFinalForm/fields/FieldError';
import TextArea from 'web-react-ui/src/reactFinalForm/fields/TextArea';
import FormSubmit from 'web-react-ui/src/reactFinalForm/FormSubmit';
import SimpleForm from 'web-react-ui/src/reactFinalForm/SimpleForm2';
import { required } from 'web-react-ui/src/reactFinalForm/validators';
import businessesModule from '../../../../modules/businesses';
import createSocialPost from './createSocialPost';
import RequestResultAlert from './RequestResultAlert';
import ToggleButtonField from './ToggleButtonField';
import CreatePostFormData from './CreatePostFormData.interface';

const VideoUploaderField = ({ name, validate, businessId }: {
  name: string,
  validate: (value: string) => string | undefined,
  businessId: string,
}) => {
  return (
    <Field name={name} validate={validate}>
      {({ input }) => {
        return (
          <VideoUploader onChange={input.onChange} businessId={businessId} />
        );
      }}
    </Field>
  );
};

const CreateCustomReel = () => {
  useEffect(
    () => {
      console.log('[mount] CreateCustomReel');
      return () => {
        console.log('[unmount] CreateCustomReel');
      };
    },
    [],
  );

  const contentType = 'reel';

  const business = useSelector(businessesModule.selectors.business.getData);

  const profile = useSelector((state: RootStateOrAny) => state.social.profile);

  const isFacebookLinked = useMemo(
    () => {
      return profile.socialAccounts.find((s: any) => s.type === 'facebook' && s.linked);
    },
    [profile],
  );

  const isInstagramLinked = useMemo(
    () => {
      return profile.socialAccounts.find((s: any) => s.type === 'instagram' && s.linked);
    },
    [profile],
  );

  const initialValues = useMemo(
    () => ({
      contentType,
    }),
    [],
  );

  const createFacebookRequest = useRequest(
    async (formData: CreatePostFormData) => {
      if (!formData.facebook) return null;

      if (createFacebookRequest.result) return createFacebookRequest.result;

      return createSocialPost({
        ...formData,
        socialNetwork: 'facebook',
        businessId: business.id,
        flow: 'draft',
      });
    },
  );
  const createInstagramRequest = useRequest(
    async (formData: CreatePostFormData) => {
      if (!formData.instagram) return null;
      if (createInstagramRequest.result) return createInstagramRequest.result;

      return createSocialPost({
        ...formData,
        socialNetwork: 'instagram',
        businessId: business.id,
        flow: 'draft',
      });
    },
  );

  const submitForm = useMemo(
    () => async (formData: CreatePostFormData) => {
      await Promise.all([
        createFacebookRequest.run(formData),
        createInstagramRequest.run(formData),
      ]);
    },
    [],
  );

  return (
    <SimpleForm onSubmit={submitForm} initialValues={initialValues} debug>
      <Flex direction="column" gap="3em" flex="1" minW="40ch" alignItems="start">
        <PageHeader textTransform="capitalize">{`Create Custom ${contentType}`}</PageHeader>

        <Flex direction="column" gap="1em" alignContent="stretch" w="100%">
          <FormControl>
            <FormLabel fontSize="lg">Social Networks</FormLabel>
            <ButtonGroup size="lg" spacing="1em">
              <ToggleButtonField
                name="facebook"
                isDisabled={createFacebookRequest.result || !isFacebookLinked}
              >
                Facebook
              </ToggleButtonField>
              <ToggleButtonField
                name="instagram"
                isDisabled={createInstagramRequest.result || !isInstagramLinked}
              >
                Instagram
              </ToggleButtonField>
            </ButtonGroup>
            <Field
              name="socialNetworks"
              validate={(_: any, formValues: CreatePostFormData) => {
                const { facebook, instagram } = formValues;
                if (facebook || instagram) return undefined;

                return 'At least one Social Network is required';
              }}
            >
              {() => (
                <FieldError name="socialNetworks" alwaysShow={false} />
              )}
            </Field>
          </FormControl>
          <RequestResultAlert request={createFacebookRequest} socialNetwork="Facebook" contentType={contentType} />
          <RequestResultAlert request={createInstagramRequest} socialNetwork="Instagram" contentType={contentType} />
        </Flex>

        <FormControl>
          <FormLabel fontSize="lg">Scheduled Date</FormLabel>
          <DateTimeField name="scheduledDate" validate={required} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="lg">Video</FormLabel>
          <VideoUploaderField name="mediaId" validate={required} businessId={business.id} />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="lg">Caption</FormLabel>
          <TextArea name="message" wrapLabel chakra />
        </FormControl>

        <FormSubmit
          ml="auto"
          colorScheme="blue"
          label={`Create ${contentType}`}
          ignoreValidity
          textTransform="capitalize"
        />
      </Flex>
    </SimpleForm>
  );
};

export default CreateCustomReel;
